import { clsx, type ClassValue } from 'clsx'
import { UseFormSetError } from 'react-hook-form'
import toast from 'react-hot-toast'
import { twMerge } from 'tailwind-merge'

import envConfig from '@/config/env-config'
import { generalInfo } from '@/types/basics.type'
import { eGeneralInfo, languages } from '@/types/enum'
import { EntityError } from '@/types/http-error.type'
import { ProductPrice } from '@/types/product.type'

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs))
}

export const normalizePath = (path: string) => {
  return path.startsWith('/') ? path.slice(1) : path
}

export function getGeneralInfo(data: generalInfo[], key: eGeneralInfo, lang: languages) {
  const name = data?.find((d) => d.setting_key === key)
  return lang === languages.vi ? name?.name : name?.name_en
}

export function loadImage(type: 'large' | 'medium', id: number) {
  const t = type ? type : 'large'
  const url = `${envConfig.FILE_URL}/upload/${t}/${id}.jpg`

  return url
}

export const splitMatchedText = (search: string, name: string, color: string = '#32C0CC') => {
  const regExp = new RegExp(search, 'gi')
  return name.replace(regExp, (match: string) => `<span style="color: ${color}">${match}</span>`)
}

export const formatCurrency = (
  price: number,
  options?: { symbol?: string; showDecimal?: boolean; separator?: string; lang?: languages; showSymbol?: boolean; curencyType?: 'VND' | 'USD' },
) => {
  const isShowSymbol = options?.showSymbol ?? true
  const symbol = options?.symbol ?? 'đ'
  const showDecimal = options?.showDecimal ?? true
  const separator = options?.separator ?? '.'

  // if (options?.lang && options?.lang === languages.en) {
  //   return `${isShowSymbol ? `${options.symbol ?? '$'} ${price}` : price}`
  // }

  if (options?.curencyType && options.curencyType === 'USD') {
    return `${isShowSymbol ? `${options.symbol ?? '$'} ${price}` : price}`
  }
  const DecimalSeparator = Number('1.2').toLocaleString().substr(1, 1)
  const priceWithComas = price?.toString()
  const arParts = String(priceWithComas).split(DecimalSeparator)
  const intPart = arParts[0].replace(/./g, function (c, i, a) {
    return i > 0 && (a.length - i) % 3 === 0 ? separator + c : c
  })
  const decPart = arParts.length > 1 ? arParts[1] : ''
  // decPart = !decPart ? decPart + '00' : ''
  return `${intPart}${showDecimal && decPart ? separator + decPart : ''} ${isShowSymbol ? symbol : ''}`
}

export const cleanText = (string: string) => {
  const patterns = [
    /(à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ)/g,
    /(ì|í|ị|ỉ|ĩ)/g,
    /(ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ)/g,
    /(è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ)/g,
    /(ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ)/g,
    /(ỳ|ý|ỵ|ỷ|ỹ)/g,
    /(đ)/g,
    /æ/g,
    /ç/gi,
    /ñ/gi,
    /%/gi,
    /[^\x00-\x7F]+/gi,
    /[^\w_ \-]+/gi,
    /^-|-$|/gi,
    /-+$/gi,
    / {2,}/g,
  ]
  const replace = ['a', 'i', 'u', 'e', 'o', 'y', 'd', 'ae', 'c', 'n', ' ', ' ', '', '', ' ']

  for (let i = 0; i < patterns.length; i++) {
    string = string.replace(patterns[i], replace[i])
  }

  return string
}

export const productPriceChange = (price: number, change: number): number => {
  if (price && change) {
    const originalPrice = price / ((100 - Math.abs(change)) / 100)
    return Math.round(originalPrice / 100) * 100 // Làm tròn đến hàng trăm
  } else {
    return 0
  }
}

const isBrowser = typeof window !== 'undefined'

export const getToken = () => (isBrowser ? localStorage.getItem('token') : null)
export const setToken = (token: string) => isBrowser && localStorage.setItem('token', token)
export const removeToken = () => isBrowser && localStorage.removeItem('token')
export const getLocale = () => (isBrowser ? localStorage.getItem('locale') : null)
export const setLocale = (locale: string) => isBrowser && localStorage.setItem('locale', locale)
export const getCurrency = () => (isBrowser ? localStorage.getItem('currency') : null)
export const setCurrency = (currency: string) => isBrowser && localStorage.setItem('currency', currency)

// lấy giá dựa trên số lượng
export const getPriceByQuantity = (quantity: number, prices: ProductPrice[]) => {
  for (let i = prices.length - 1; i >= 0; i--) {
    if (quantity >= prices[i].quantity) {
      return prices[i]
    }
  }

  return null // Trường hợp input nhỏ hơn giá trị đầu tiên trong mảng
}

export const valueByMultiples = (value: number, multiples: number): number => {
  // nếu dư thì cộng thêm multiples
  let val
  const remainder = value % multiples

  if (remainder > 0) {
    val = value + multiples - remainder
  } else {
    val = value
  }

  // if (remainder <= multiples / 2) {
  //   // Round down to the nearest multiple
  //   val = value - remainder
  // } else {
  //   // Round up to the nearest multiple
  //   val = value + multiples - remainder
  // }

  return val
}

export const numberUnit = (value: number): string => {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) => reject(error)
  })
}

export const base64ToFile = (base64: string, fileName: string): File => {
  const arr = base64.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], fileName, { type: mime })
}

export const handleApiError = ({ error, setError, content }: { error: any; setError?: UseFormSetError<any>; content?: string | undefined }) => {
  if (error instanceof EntityError && setError) {
    for (const [key, value] of Object.entries(error.payload)) {
      setError(key, { message: value as string })
    }
  } else {
    // Provide a default message if specific one isn't available
    const errorMessage = content ?? error?.payload?.message ?? error?.message ?? 'An unexpected error occurred'
    toast.error(errorMessage)
  }
}

export const extractPathnameLocale = (pathname: string): string => {
  const path = pathname.split('/').filter(Boolean) // Loại bỏ phần tử rỗng
  if (path.length > 1 && [languages.vi, languages.en].includes(path[0] as languages)) {
    return `/${path.slice(1).join('/')}` || '/' // Nếu có path -> trả về path, nếu không trả về '/'
  } else if ([languages.vi, languages.en].includes(path[0] as languages)) {
    return '/' // Nếu có locale nhưng không có path -> trả về '/'
  }

  return pathname // Nếu không có locale, trả về nguyên pathname
}

export const dateFormat = (data: string, timeZone: string = 'Asia/Ho_Chi_Minh') => {
  const date = new Date(data)

  const formattedDate = date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: timeZone })
  const formattedDateNoYear = date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', timeZone: timeZone })
  const formattedTime = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23', timeZone: timeZone })
  const formattedTimeNoSec = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: timeZone })

  return { formattedDate, formattedTime, formattedTimeNoSec, formattedDateNoYear }
}

export const roundToNearestHundred = (number: number, currency: 'VND' | 'USD') => {
  if (currency === 'VND') return number
  return Math.round(number / 100) * 100
}

export const toPositiveVND = (price: number, currency: 'VND' | 'USD'): number => {
  if (currency === 'VND') {
    return Math.abs(Math.trunc(price))
  }
  return price
}

export const dateFormatForInput = (dateString: number | null = null) => {
  const date = dateString ? new Date(dateString) : new Date()
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${year}-${month}-${day}`
}

export const priceVND = (price: number, exchange: number, currency: 'VND' | 'USD'): number => {
  if (currency === 'USD' && exchange) {
    const newPrice = Math.round(price * exchange)
    return newPrice
  } else {
    return price
  }
}

export const priceByLang = (fee: number, exchange: number, currency: 'VND' | 'USD'): number => {
  if (currency === 'VND') return fee

  const fee_usd = fee / exchange
  return +fee_usd.toFixed(3)
}

// đổi tiền tệ từ vnd sang usd nếu đơn hàng dùng loại tiền usd
export const priceByLangOrder = (price: number, exchange: number, currency: number) => {
  if (exchange && currency === 2) {
    const value = price / exchange
    const r = value < 0.001 ? 5 : value < 0.01 ? 4 : 3
    const newPrice = roundToDecimal(value, r)
    return `$ ${newPrice}`
  } else {
    return `${numberUnit(price)} đ`
  }
}

export const roundToDecimal = (value: number, decimals: number) => {
  const factor = Math.pow(10, decimals)
  return Math.round(value * factor) / factor
}

export const dateFormatString = (dateString: string | null) => {
  const date = dateString ? new Date(dateString) : new Date()
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

// export const maskEmail = (email: string): string => {
//   const [localPart, domain] = email.split('@')
//   const maskedLocal = '*'.repeat(localPart.length - 2) + localPart.slice(-2)

//   const domainParts = domain.split('.')
//   if (domainParts.length > 1) {
//     const domainName = domainParts[0]
//     const tld = domainParts.slice(1).join('.')
//     const maskedDomain = '*'.repeat(domainName.length - 2) + domainName.slice(-2)
//     return `${maskedLocal}@${maskedDomain}.${tld}`
//   }

//   return `${maskedLocal}@${domain}`
// }

export const maskEmail = (email: string): string => {
  const [localPart, domain] = email.split('@')

  const maskedLocal =
    '*'.repeat(Math.max(0, localPart.length - 2)) +
    localPart.slice(-2)

  const domainParts = domain.split('.')

  if (domainParts.length > 1) {
    const domainName = domainParts[0]
    const tld = domainParts.slice(1).join('.')

    const maskedDomain =
      '*'.repeat(Math.max(0, domainName.length - 2)) +
      domainName.slice(-2)

    return `${maskedLocal}@${maskedDomain}.${tld}`
  }

  return `${maskedLocal}@${domain}`
}

export function loadAvatar(id: number) {
  const url = `${envConfig.FILE_URL}/photo/medium/${id}.jpg`
  return url
}

export const dateFormatEnd = (dateStr: string) => {
  const date = new Date(dateStr)

  const formattedDate = new Intl.DateTimeFormat('en-US', {
    weekday: 'short', // Tên ngày trong tuần (Mon, Tue,...)
    year: 'numeric', // Năm đầy đủ (2024)
    month: 'short', // Tháng dạng rút gọn (Nov)
    day: 'numeric', // Ngày trong tháng (14)
    hour: '2-digit', // Giờ (10)
    minute: '2-digit', // Phút (34)
    hour12: false, // Định dạng 12 giờ (AM/PM)
  }).format(date)

  return formattedDate
}

export const stripHtmlWithRegex = (html: string): string => {
  html = html.replace(/<[^>]*>(&nbsp;|\s)*<\/[^>]*>/g, '')
  return html.replace(/<\/?[^>]+(>|$)/g, '').trim()
}

export const getCookie = (name: string): string | null => {
  if (typeof window === 'undefined') return null

  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? decodeURIComponent(match[2]) : null
}

export const setCookie = (name: string, value: string) => {
  if (typeof window === 'undefined') return

  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Lax`
}

export const createSetCookieHeader = (token: string, maxAge: number) => {
  const isProduction = envConfig.NODE_ENV === 'production'

  const cookieOptions = [
    isProduction ? `_Secure.token=${token}` : `_Dev.token=${token}`,
    'Path=/',
    `Max-Age=${maxAge}`,
    'HttpOnly',
    'SameSite=Lax',
    isProduction ? 'Secure' : '', // Chỉ thêm Secure nếu production
  ].filter(Boolean) // Loại bỏ string rỗng

  return cookieOptions.join('; ')
}

export const isNumeric = (str: string): boolean => {
  // kiểm tra chuỗi không rỗng và chỉ gồm ký tự 0-9
  return /^[0-9]+$/.test(str)
}

export const isValidPath = (pathname: string, prefix: string) => {
  return pathname.startsWith(`${prefix}`) && new RegExp(`^\\${prefix}\\/\\d+$`).test(pathname)
}

export const formatedAddress = (address: string) => {
  return address.replaceAll(',', '').replace(/\s+/g, ' ').trim()
}

export const isValidVietnamPhone = (phone: string): boolean => {
  if (!phone) return false

  const cleanPhone = phone.replace(/\s|-/g, '')

  // Bắt đầu bằng 0, theo sau là 9 chữ số
  // const regex = /^0(3|5|7|8|9)\d{8}$/ giới hạn đầu số di động 03 05 07 08 09
  const regex = /^0\d{9}$/
  return regex.test(cleanPhone)
}


// xóa giỏ hàng trước khi tạo lại giỏ hàng mới
export const emailToRemoveCart = ['jp.chaveriat@belgacom.net', 'duongnd1402@gmail.com']

export const roundUpToThousand = (num: number) => Math.ceil(num / 1000) * 1000

export const calQty = (quantity: number, multiples: number) => {
  const quotient = Math.trunc(quantity / multiples)
  const remainder = quantity % multiples
  return remainder > 0 ? multiples * (quotient + 1) : quantity
}

const ones = ['', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín']
const tens = ['', 'mười', 'hai mươi', 'ba mươi', 'bốn mươi', 'năm mươi', 'sáu mươi', 'bảy mươi', 'tám mươi', 'chín mươi']

function readHundreds(n: number): string {
  n = Math.floor(Math.abs(n))
  if (n === 0) return ''
  const hundred = Math.floor(n / 100)
  const ten = Math.floor((n % 100) / 10)
  const one = n % 10

  let result = ''

  if (hundred > 0) {
    result += ones[hundred] + ' trăm'
  }

  if (ten === 0 && one === 0) return result

  if (ten === 0) {
    if (hundred > 0) {
      result += ' lẻ ' + ones[one]
    } else {
      result += ' ' + ones[one]
    }
  } else if (ten === 1) {
    result += ' mười'
    if (one === 5) result += ' lăm'
    else if (one > 0) result += ' ' + ones[one]
  } else {
    result += ' ' + tens[ten]
    if (one === 5) result += ' lăm'
    else if (one === 1) result += ' mốt'
    else if (one > 0) result += ' ' + ones[one]
  }

  return result.trim()
}

export const numberToWords = (n: number, type?: string): string => {
  const safeN = Math.round(Number(n) || 0)
  const suffix = type ?? ' đồng'

  if (safeN === 0) return 'Không' + suffix

  const units = ['', ' nghìn', ' triệu', ' tỷ']
  const parts: string[] = []

  // Xử lý phần tỷ (có thể > 999 tỷ)
  const billion = Math.floor(safeN / 1_000_000_000)
  const remainder = safeN % 1_000_000_000

  if (billion > 0) {
    parts.push(readHundreds(billion) + ' tỷ')
  }

  const sub = [Math.floor(remainder / 1_000_000), Math.floor((remainder % 1_000_000) / 1_000), remainder % 1_000]

  sub.forEach((val, idx) => {
    if (val > 0) {
      parts.push(readHundreds(val) + units[2 - idx])
    }
  })

  const words = parts.join(' ').trim()
  const capitalized = words.charAt(0).toUpperCase() + words.slice(1)

  return capitalized + suffix
}

export const formatPhone = (phone: string, type: 'mobile' | 'phone' = 'mobile') => {
  const cleaned = phone.replace(/\D/g, '')
  if (type === 'mobile') {
    return cleaned.replace(/^(\d{4})(\d{3})(\d{3})$/, '$1 $2 $3')
  } else {
    return cleaned.replace(/^(\d{3})(\d{4})(\d{4})$/, '($1) $2 $3')
  }
}

export const darkenColor = (color: string, amount = 20) => {
  const hex = color.replace('#', '')

  if (hex.length !== 6) return color

  const r = Math.max(0, parseInt(hex.slice(0, 2), 16) - amount)
  const g = Math.max(0, parseInt(hex.slice(2, 4), 16) - amount)
  const b = Math.max(0, parseInt(hex.slice(4, 6), 16) - amount)

  return `#${r.toString(16).padStart(2, '0')}${g
    .toString(16)
    .padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}